import { useMemo } from 'react';

import { useMissionList } from '../hooks/useMissionList.js';
import { resolvePlayerMark, useBlipFeed } from '../hooks/useBlipFeed.js';
import { MARKER_COLORS } from './mapRender.js';

/**
 * @file THE MISSION LIST — every job the player has been offered, split into
 * story and side work, with the one they are tracking picked out and a button
 * to change it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TRACKING IS THE WHOLE POINT OF THIS SCREEN
 * ─────────────────────────────────────────────────────────────────────────────
 * The panel and the highlighted marker follow the TRACKED mission
 * (`engine/systems/Missions.js` keeps that separate from the ACTIVE one), and
 * until there is a screen to change it on, tracking is a feature nobody can
 * reach. So the row's button emits `'mission:track'` and the director answers
 * by republishing `'mission:list'` — one-way in, one-way out, and this
 * component keeps no state of its own.
 *
 * ⚠ IT ASKS ON MOUNT, and that is not optional. `'mission:list'` is published
 * ON CHANGE, this screen mounts long after the last one, and the pause menu has
 * already frozen the loop — so a component that only listened would render an
 * empty list forever (it did, first time out). That rule now lives in ONE place,
 * `hooks/useMissionList.js`, which is the subscription AND the ask; its header
 * carries the full story. This file just uses it, and so does the phone.
 *
 * ⚠ THE DISTANCE IS COMPUTED HERE, not published. `'mission:list'` fires on
 * change and a distance changes every step, so putting it in the payload would
 * turn an event channel into a 60 Hz one. The list carries each mission's
 * `x`/`z` instead and this reads the player's position off the blip feed, which
 * is already subscribed for the map.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO EXPORTS: THE SCREEN, AND THE ROWS
 * ─────────────────────────────────────────────────────────────────────────────
 * The default export is the PAUSE MENU's screen — a full-window `inset-0` root,
 * a header with a Back button, an `Esc — back` line. `MissionRows` is the same
 * list with none of that, so the phone can show missions inside a card that
 * already has its own header, its own close key and its own navigation.
 *
 * ⚠ IT IS A SECOND COMPONENT, NOT A `chrome={false}` PROP. Four separate things
 * would have had to be conditionalised (the root, the header, the Back button,
 * the Esc line), and the moment a component takes a flag that turns off its own
 * navigation it has grown TWO navigation models and every future change has to
 * be reasoned about twice. The split costs one export and no branches.
 *
 * ⚠ `MissionRows` OWNS NO SUBSCRIPTION. It is handed `missions` and `me`, so a
 * host with a list already in hand does not open a second one — and so the empty
 * case is decided once, here, rather than by whichever screen forgot.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {Function} props.onBack
 */

/** Display wording per `MISSION_STATE`. */
const STATE_LABEL = Object.freeze({
  locked: 'Locked',
  available: 'Available',
  briefing: 'Briefing',
  active: 'In progress',
  complete: 'Completed',
  failed: 'Failed',
});

const STATE_TONE = Object.freeze({
  locked: 'text-hud-faint/60',
  available: 'text-hud-dim',
  briefing: 'text-gold',
  active: 'text-gold',
  complete: 'text-[#5fbf7a]',
  failed: 'text-[#ef6b5e]',
});

export default function MissionList({ engine, onBack }) {
  const list = useMissionList(engine);
  const feed = useBlipFeed(engine);

  const missions = list?.missions ?? [];
  const me = resolvePlayerMark(engine, feed.current, { x: 0, z: 0, yaw: 0, live: false });

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-50 flex flex-col bg-void/95 backdrop-blur-[2px]"
    >
      <header className="flex items-baseline justify-between px-6 pt-5 sm:px-10">
        <div>
          <div className="gta-outline font-display text-2xl text-gold">MISSIONS</div>
          <div className="font-hud text-[11px] uppercase tracking-[0.34em] text-hud-faint">
            {missions.filter((m) => m.state === 'complete').length} of {missions.length} complete
          </div>
        </div>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onBack}
          className="rounded-[3px] border border-hairline bg-ink/85 px-4 py-2 font-display text-sm uppercase tracking-wide text-hud transition hover:border-gold hover:text-gold"
        >
          Back
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 pt-6 sm:px-10">
        <div className="mx-auto flex max-w-3xl flex-col gap-7">
          <MissionRows missions={missions} engine={engine} me={me} />
        </div>
      </div>

      <p className="pointer-events-none absolute bottom-5 right-6 select-none font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint">
        Esc — back
      </p>
    </div>
  );
}

/**
 * The list ITSELF — the story/side/completed split and nothing else. No root
 * positioning, no header, no Back button, no key hint: whoever renders this owns
 * all four. See this file's header for why it is an export rather than a flag.
 *
 * ⚠ NO SUBSCRIPTION LIVES HERE. `missions` and `me` are props, so a host that
 * already holds a `'mission:list'` payload (the phone panel shows a completion
 * count beside these rows) does not open a second subscription to the same
 * channel and cannot render a different generation of the same list twice on one
 * screen.
 *
 * The order of the three groups is the reading order of the screen: what the
 * story wants next, then what is optional, then what is behind you. The empty
 * case is decided HERE, once, rather than by whichever host remembered — a
 * screen with no missions must say so, never draw an empty box.
 *
 * @param {{missions: any[], engine: any, me: any}} props
 */
export function MissionRows({ missions, engine, me }) {
  const rows = Array.isArray(missions) ? missions : [];

  const { critical, optional, done } = useMemo(() => {
    /** @type {any[]} */
    const c = [];
    /** @type {any[]} */
    const o = [];
    /** @type {any[]} */
    const d = [];
    for (const m of rows) {
      if (m.state === 'complete') d.push(m);
      else if (m.critical) c.push(m);
      else o.push(m);
    }
    return { critical: c, optional: o, done: d };
  }, [rows]);

  return (
    <>
      {rows.length === 0 ? (
        <p className="font-hud text-[13px] text-hud-faint">No missions in this scene.</p>
      ) : null}

      <Group title="Story" rows={critical} engine={engine} me={me} />
      <Group title="Side jobs" rows={optional} engine={engine} me={me} />
      <Group title="Completed" rows={done} engine={engine} me={me} />
    </>
  );
}

/** @param {{title: string, rows: any[], engine: any, me: any}} props */
function Group({ title, rows, engine, me }) {
  if (!rows.length) return null;
  return (
    <section>
      <h2 className="mb-2 font-hud text-[10px] font-bold uppercase tracking-[0.3em] text-hud-faint">
        {title}
      </h2>
      <ul className="flex flex-col gap-1.5">
        {rows.map((m) => (
          <Row key={m.id} m={m} engine={engine} me={me} />
        ))}
      </ul>
    </section>
  );
}

/** @param {{m: any, engine: any, me: any}} props */
function Row({ m, engine, me }) {
  const locked = m.state === 'locked';
  const complete = m.state === 'complete';
  const canTrack = !locked && !complete;
  const dist = Math.round(Math.hypot((m.x ?? 0) - me.x, (m.z ?? 0) - me.z));

  return (
    <li
      className={`relative overflow-hidden rounded-[3px] border px-4 py-3 transition ${
        m.tracked
          ? 'border-gold/70 bg-steel/60'
          : 'border-hairline bg-ink/70'
      } ${locked ? 'opacity-45' : ''}`}
    >
      {/* The tracked rule, mirroring the map's tracked halo. */}
      {m.tracked ? (
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-gold" />
      ) : null}

      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-[3px] inline-block h-3 w-3 shrink-0 rounded-full border-2 bg-transparent"
          style={{
            borderColor: complete
              ? '#5fbf7a'
              : MARKER_COLORS[m.critical ? 'giver' : 'giver_optional'],
          }}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate font-display text-[15px] uppercase tracking-wide text-hud">
              {locked ? '???' : m.name}
            </span>
            <span className={`shrink-0 font-hud text-[10px] uppercase tracking-[0.2em] ${STATE_TONE[m.state] ?? 'text-hud-faint'}`}>
              {STATE_LABEL[m.state] ?? m.state}
            </span>
          </div>

          <p className="mt-0.5 font-hud text-[12.5px] leading-snug text-hud-dim">
            {locked ? 'Complete an earlier job to unlock this.' : m.text || m.blurb}
          </p>

          {!locked && !complete ? (
            <div className="mt-1 font-hud text-[11px] tabular-nums tracking-[0.14em] text-hud-faint">
              {dist} m away
              {m.state === 'active' && m.stages > 1 ? ` · stage ${m.stage + 1}/${m.stages}` : ''}
            </div>
          ) : null}
        </div>

        {canTrack ? (
          <button
            type="button"
            disabled={m.tracked}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => engine?.bus?.emit?.('mission:track', { id: m.id })}
            className={`shrink-0 rounded-[3px] border px-3 py-1.5 font-hud text-[10px] uppercase tracking-[0.2em] transition ${
              m.tracked
                ? 'cursor-default border-gold/50 text-gold'
                : 'border-hairline text-hud-dim hover:border-gold hover:text-gold'
            }`}
          >
            {m.tracked ? 'Tracked' : 'Track'}
          </button>
        ) : null}
      </div>
    </li>
  );
}
